import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';

const EditAdmin = () => {
  const navigate = useNavigate();
  const { adminId } = useParams(); // Get the admin ID from the URL

  // State variables for admin fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  // Password should not be pre-filled for security reasons
  const [siteId, setSiteId] = useState('');

  // State variables for API calls and errors
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // State variable to store the list of sites
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState("");

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/sites');
        setSites(response.data.data || []);
      } catch (err) {
        console.error("Error fetching sites:", err.response || err.message);
        setSitesError("Failed to load sites.");
      } finally {
        setSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  useEffect(() => {
    const fetchAdmin = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/users/${adminId}`);
        const adminData = response.data.data;

        setName(adminData.name);
        setEmail(adminData.email);
        setMobile(adminData.mobile);
        setSiteId(adminData.site_id || ''); // Ensure site_id is handled correctly
      } catch (err) {
        console.error("Error fetching admin:", err.response || err.message);
        setError("Failed to load admin data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [adminId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.patch(`/users/${adminId}`, { // Use PATCH for updates
        name: name,
        email: email,
        mobile: mobile,
        site_id: siteId,
      });

      if (response.data.success) {
        setSuccessMessage('Administrator updated successfully!');
        navigate('/super-admin/admins');
      } else {
        setError(response.data.message || 'Failed to update administrator.');
      }
    } catch (err) {
      setError('An error occurred while updating the administrator.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>Edit Administrator</h1>
      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formName">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formMobile">
            <Form.Label>Mobile</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formSiteId">
            <Form.Label>Site</Form.Label>
            {sitesLoading ? (
              <Spinner animation="border" size="sm" />
            ) : sitesError ? (
              <Alert variant="danger">{sitesError}</Alert>
            ) : (
              <Form.Control
                as="select"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                required
              >
                <option value="">Select a Site</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.brand_name}
                  </option>
                ))}
              </Form.Control>
            )}
          </Form.Group>

          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Updating...' : 'Update Administrator'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/super-admin/admins')}>
            Cancel
          </Button>
        </Form>
      )}
    </Container>
  );
};

export default EditAdmin;
